import { clsx } from 'clsx';
import { Children, type ComponentPropsWithoutRef, forwardRef, type ReactNode } from 'react';

import * as styles from './spotlight.css';

export type SpotlightListProps = ComponentPropsWithoutRef<'div'> & {
  /** Shown (instead of children) when there are no results. */
  empty?: ReactNode;
};

/**
 * The scrollable results region below the input. Renders `empty` when it has no
 * children — callers pass their own empty-state node.
 */
export const SpotlightList = forwardRef<HTMLDivElement, SpotlightListProps>(
  ({ children, className, empty, 'aria-label': ariaLabel, id, ...props }, ref) => {
    const isEmpty = Children.toArray(children).length === 0;
    // An empty results region is not a listbox (a listbox must contain only options),
    // so present the message as a status instead of nesting it under role="listbox".
    // The listbox label/id belong to the listbox role only — keep them off the
    // role-less empty wrapper (they'd otherwise be an ignored label on nothing).
    return isEmpty ? (
      <div className={clsx(styles.list, className)} ref={ref} {...props}>
        <div className={styles.empty} role="status">
          {empty}
        </div>
      </div>
    ) : (
      <div
        aria-label={ariaLabel}
        className={clsx(styles.list, className)}
        id={id}
        ref={ref}
        role="listbox"
        {...props}
      >
        {children}
      </div>
    );
  }
);

SpotlightList.displayName = 'SpotlightList';
